import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Analytics } from "@/lib/analytics";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, Clock, Star, ShieldCheck, MessageCircle, Tag, X, ArrowRight, PlayCircle,
  GraduationCap, Briefcase, Users, FileText, Sparkles, CheckCircle2, Award, Headphones,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";
import UrgencyBar from "@/components/UrgencyBar";
import FreeSyllabusCTA from "@/components/FreeSyllabusCTA";
import {
  saveReturnUrl,
  savePendingIntent,
  consumePendingIntent,
} from "@/lib/postLoginRedirect";
import { getCoursePricing, formatINR, getAllowedPaymentOptions } from "@/lib/pricing";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };
const WHATSAPP_BASE = "https://wa.me/919131341638?text=";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [pageCfg, setPageCfg] = useState({ faqs: [], mentor_blocks: [], custom_sections: [], testimonials: [], hero_subtitle: "", hero_badge_text: "", banner_text: "", banner_cta_text: "", banner_cta_url: "" });
  const [notFound, setNotFound] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [paying, setPaying] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState("full");  // 'full' | 'partial_50' | 'installment_3'

  useEffect(() => {
    // Reset state immediately on id change so user sees loading skeleton (not stale data)
    setCourse(null);
    setNotFound(false);
    setEnrolled(false);
    setAppliedCoupon(null);
    setCouponInput("");
    setPageCfg({ faqs: [], mentor_blocks: [], custom_sections: [], testimonials: [], hero_subtitle: "", hero_badge_text: "", banner_text: "", banner_cta_text: "", banner_cta_url: "" });
    setPaymentPlan("full");
    // Scroll to top on route change
    window.scrollTo({ top: 0, behavior: "instant" });

    api.get(`/courses/${id}`)
      .then(({ data }) => {
        setCourse(data);
        Analytics.viewCourse({
          course_id: id,
          course_title: data?.title,
          value: getCoursePricing(data).final,
          currency: "INR",
        });
      })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else toast.error("Could not load course details");
      });
    // Load custom page config (public, no auth needed)
    api.get(`/course-page/${id}`).then(({ data }) => setPageCfg((p) => ({ ...p, ...data }))).catch(() => {});
    if (user) api.get("/enrollments").then(({ data }) => setEnrolled(data.some((e) => e.course_id === id))).catch(() => {});
  }, [id, user]);

  // After course loads, refine enrolled check against the actual UUID
  // (URL might be a slug — enrollments store the real course UUID).
  useEffect(() => {
    if (user && course?.id && course.id !== id) {
      api.get("/enrollments").then(({ data }) => {
        if (data.some((e) => e.course_id === course.id)) setEnrolled(true);
      }).catch(() => {});
    }
  }, [course, user, id]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Please enter a coupon code"); return; }
    if (!user) {
      saveReturnUrl(window.location.pathname + window.location.search);
      savePendingIntent({ type: "apply_coupon", course_slug_or_id: id, coupon_code: code });
      toast.message("Please login to apply this coupon");
      nav("/login", { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }
    if (!course?.id) { toast.error("Course still loading — please retry"); return; }
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, course_id: course.id });
      setAppliedCoupon(data);
      toast.success(`Coupon applied — ₹${data.discount.toLocaleString("en-IN")} off!`);
    } catch (e) { setAppliedCoupon(null); toast.error(e.response?.data?.detail || "Invalid coupon"); }
    finally { setApplyingCoupon(false); }
  };

  const clearCoupon = () => { setAppliedCoupon(null); setCouponInput(""); };

  const handleEnroll = async (overrides) => {
    if (!user) {
      saveReturnUrl(window.location.pathname + window.location.search);
      savePendingIntent({
        type: "enroll",
        course_slug_or_id: id,
        payment_plan: paymentPlan,
        coupon_code: appliedCoupon?.code || null,
      });
      toast.message("Login karke aap turant checkout pe pohonch jayenge");
      nav("/login", { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }
    if (!course?.id) { toast.error("Course still loading — please retry"); return; }
    const plan = overrides?.plan ?? paymentPlan;
    const couponObj = overrides?.coupon !== undefined ? overrides.coupon : appliedCoupon;
    setPaying(true);
    try {
      const body = { course_id: course.id, payment_plan: plan };
      if (couponObj?.code) body.coupon_code = couponObj.code;
      const { data } = await api.post("/payments/create-order", body);
      const { final: baseFinal } = getCoursePricing(course);
      const txValue = couponObj?.final ?? baseFinal;
      Analytics.beginCheckout({ course_id: course.id, course_title: course.title, value: txValue, currency: "INR", coupon: couponObj?.code });
      if (data.free) {
        toast.success(couponObj ? "Coupon ne 100% off kar diya — enrolled!" : "Enrolled in free course!");
        setEnrolled(true);
        Analytics.purchase({ course_id: id, course_title: course.title, value: 0, currency: "INR", coupon: couponObj?.code, free: true });
        return;
      }
      if (!data.mock && window.Razorpay) {
        const rzp = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
          name: "CodeWithKamlesh", description: couponObj ? `${course.title} (Coupon: ${couponObj.code})` : course.title,
          prefill: { name: user.name, email: user.email }, theme: { color: "#FF0066" },
          handler: async (resp) => {
            try {
              await api.post("/payments/verify", { razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
              toast.success("Payment successful — enrolled!"); setEnrolled(true);
              Analytics.purchase({
                transaction_id: resp.razorpay_payment_id,
                course_id: id,
                course_title: course.title,
                value: txValue,
                currency: "INR",
                coupon: couponObj?.code,
              });
              setTimeout(() => { nav("/dashboard/student"); }, 1500);
            } catch { toast.error("Verification failed"); }
          },
        });
        rzp.open();
      } else {
        toast.message("Demo checkout: confirming payment…");
        await new Promise((r) => setTimeout(r, 900));
        await api.post("/payments/verify", { razorpay_order_id: data.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: "mock_signature" });
        toast.success("Demo payment successful — enrolled!"); setEnrolled(true);
        Analytics.purchase({ transaction_id: `mock_${Date.now()}`, course_id: id, course_title: course.title, value: txValue, currency: "INR", mock: true });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to enroll"); }
    finally { setPaying(false); }
  };

  // Post-login resume: if user just logged in with a pending intent
  // (enroll / apply_coupon) for THIS course, auto-execute it once.
  const intentHandledRef = useRef(false);
  useEffect(() => {
    if (intentHandledRef.current) return;
    if (!user || !course?.id) return;
    // We can only safely consume the intent when the page is for the
    // same course the user originally clicked.
    const stored = sessionStorage.getItem("cwk_pending_intent");
    if (!stored) { intentHandledRef.current = true; return; }
    let intent;
    try { intent = JSON.parse(stored); } catch { intent = null; }
    if (!intent || (intent.course_slug_or_id && intent.course_slug_or_id !== id)) {
      // Different page than where the intent was set — leave for that page.
      intentHandledRef.current = true;
      return;
    }
    consumePendingIntent();
    intentHandledRef.current = true;

    if (intent.type === "apply_coupon" && intent.coupon_code) {
      setCouponInput(intent.coupon_code);
      // Validate the coupon, then auto-trigger enroll if user originally
      // wanted enroll. Plain apply_coupon just leaves the field populated.
      (async () => {
        try {
          const { data } = await api.post("/coupons/validate", { code: intent.coupon_code, course_id: course.id });
          setAppliedCoupon(data);
          toast.success(`Welcome back — coupon "${intent.coupon_code}" applied (₹${data.discount.toLocaleString("en-IN")} off)`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Saved coupon is no longer valid");
        }
      })();
      return;
    }

    if (intent.type === "enroll") {
      const plan = intent.payment_plan || "full";
      setPaymentPlan(plan);
      (async () => {
        let couponObj = null;
        if (intent.coupon_code) {
          try {
            const { data } = await api.post("/coupons/validate", { code: intent.coupon_code, course_id: course.id });
            couponObj = data;
            setCouponInput(intent.coupon_code);
            setAppliedCoupon(data);
          } catch { /* coupon expired/invalid — continue without it */ }
        }
        toast.success("Welcome back — checkout phir se shuru kar raha hu…");
        // Run the actual enrolment with the persisted plan + coupon.
        handleEnroll({ plan, coupon: couponObj });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, course, id]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-[#050505] text-white flex items-center justify-center p-6" style={fontStack}>
        <div className="max-w-md w-full bg-[#121212] border border-white/10 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
            <ArrowLeft className="w-7 h-7 text-amber-300" />
          </div>
          <h1 className="text-2xl font-bold mt-4" style={displayStack}>Course not found</h1>
          <p className="text-sm text-white/60 mt-2">This course may have been removed, archived, or the link is incorrect.</p>
          <Link to="/courses" className="mt-5 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition">Browse all courses</Link>
        </div>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="bg-[#050505] text-white min-h-screen" style={fontStack}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid lg:grid-cols-12 gap-10 animate-pulse">
            <div className="lg:col-span-8 space-y-4">
              <div className="h-4 w-32 bg-white/5 rounded" />
              <div className="h-12 w-3/4 bg-white/5 rounded-lg" />
              <div className="h-4 w-2/3 bg-white/5 rounded" />
              <div className="h-4 w-1/2 bg-white/5 rounded" />
              <div className="grid grid-cols-4 gap-3 mt-6">
                {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-white/5 rounded-lg" />)}
              </div>
            </div>
            <div className="lg:col-span-4 h-96 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  const { mrp, final: price, hasDiscount, percentOff, isFree } = getCoursePricing(course);
  const finalPrice = appliedCoupon?.final ?? price;
  const waText = `Hi%20Sir%2C%20I%20want%20to%20enroll%20in%20${encodeURIComponent(course.title || "your course")}%20—%20can%20you%20guide%20me%3F`;

  const learnings = (Array.isArray(course.modules) && course.modules.length ? course.modules.slice(0, 8) : null) || [
    "Run live Meta Ads and Google Ads campaigns",
    "Build SEO-optimised WordPress websites",
    "Rank websites on Google search results",
    "Generate leads using funnels and landing pages",
    "Use AI tools to scale content and campaigns",
    "Analyse data with Google Analytics & Tag Manager",
  ];

  return (
    <div className="bg-[#050505] text-white min-h-screen pb-32 md:pb-0" style={fontStack}>
      {/* ===== Promo Banner (admin customizable) ===== */}
      {pageCfg.banner_text && (
        <div className="bg-gradient-to-r from-[#FF0066] via-pink-500 to-amber-500 text-white text-center py-2.5 px-4" data-testid="promo-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap text-sm">
            <span className="font-semibold">{pageCfg.banner_text}</span>
            {pageCfg.banner_cta_text && pageCfg.banner_cta_url && (
              <a href={pageCfg.banner_cta_url} className="px-3 py-0.5 rounded-full bg-white text-[#FF0066] text-xs font-bold hover:bg-white/90">{pageCfg.banner_cta_text} →</a>
            )}
          </div>
        </div>
      )}
      {/* ===== Urgency bar (countdown + scarcity) ===== */}
      <UrgencyBar
        nextBatchDate={course.next_batch_date}
        seatsLeft={course.seats_left ?? 7}
        seatsTotal={course.seats_total ?? 20}
      />
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF0066]/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6" data-testid="back-to-courses"><ArrowLeft className="w-4 h-4" /> Back to courses</Link>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-xs font-medium text-white/80">{course.category || "Digital Marketing"}</span>
                {course.badge && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FF0066]/15 border border-[#FF0066]/30 text-xs font-bold text-[#FF0066] uppercase tracking-wider" data-testid="course-badge">
                    <Sparkles className="w-3 h-3" /> {course.badge}
                  </span>
                )}
                {course.level && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-400/25 text-xs font-medium text-emerald-300" data-testid="course-level">{course.level}</span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight" style={displayStack} data-testid="course-title">{course.title}</h1>
              {course.tagline && <p className="mt-3 text-base md:text-lg text-[#FF0066]/90 font-medium leading-snug max-w-2xl" data-testid="course-tagline">{course.tagline}</p>}
              {course.description && <p className="mt-4 text-sm md:text-base text-white/70 leading-relaxed max-w-2xl">{course.description}</p>}
              {course.outcome && (
                <div className="mt-5 inline-flex items-start gap-2.5 bg-emerald-500/10 border border-emerald-400/25 rounded-lg px-4 py-2.5 max-w-2xl" data-testid="course-outcome">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Outcome</div>
                    <div className="text-sm text-white/85 leading-snug">{course.outcome}</div>
                  </div>
                </div>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/70">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#FF0066]" /> {course.duration_weeks || 12} weeks</span>
                <span className="inline-flex items-center gap-1.5"><PlayCircle className="w-4 h-4 text-[#FF0066]" /> Live online + Indore centre</span>
                <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-current" /> {course.rating || 4.8} ({course.enrolled_count || 50}+ students)</span>
                <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-[#FF0066]" /> Certificate</span>
              </div>
              <div className="mt-5">
                <FreeSyllabusCTA courseId={id} courseTitle={course.title} />
              </div>
              {Array.isArray(course.tags) && course.tags.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-1.5" data-testid="course-tags">
                  {course.tags.slice(0, 10).map((t) => (
                    <span key={t} className="px-2 py-0.5 rounded-md bg-white/5 text-[11px] text-white/70 border border-white/10">{t}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop sticky purchase card */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 bg-[#121212] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-[#FF0066]/10" data-testid="purchase-card">
                {course.image_url ? (
                  <div className="relative aspect-[16/10] bg-[#0a0a0a] border-b border-white/10">
                    <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" data-testid="course-thumbnail" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                  </div>
                ) : (
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-[#FF0066]/30 via-[#FF0066]/10 to-[#050505] border-b border-white/10 flex items-center justify-center">
                    <GraduationCap className="w-16 h-16 text-[#FF0066]/40" />
                  </div>
                )}
                <div className="p-6">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {isFree ? <span className="text-3xl font-extrabold text-[#FF0066]" style={displayStack}>Free</span> : appliedCoupon ? (
                    <>
                      <span className="text-3xl font-extrabold text-[#FF0066]" style={displayStack} data-testid="detail-final-price">{formatINR(finalPrice, { showFree: false })}</span>
                      <span className="text-sm text-white/40 line-through">{formatINR(price, { showFree: false })}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-extrabold text-white" style={displayStack} data-testid="detail-price">{formatINR(price, { showFree: false })}</span>
                      {hasDiscount && (
                        <>
                          <span className="text-sm text-white/40 line-through" data-testid="detail-mrp">{formatINR(mrp, { showFree: false })}</span>
                          <span className="text-xs text-emerald-300 font-semibold">{percentOff}% off</span>
                        </>
                      )}
                    </>
                  )}
                </div>
                {appliedCoupon && <div className="mt-1 text-xs text-[#FF0066] font-medium">You save {formatINR(appliedCoupon.discount, { showFree: false })}</div>}

                {/* Coupon */}
                {!isFree && !enrolled && (
                  <div className="mt-4" data-testid="coupon-box">
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#FF0066]" />
                          <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }} placeholder="Coupon code" className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm font-mono uppercase text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]" data-testid="coupon-input" />
                        </div>
                        <button onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()} className="bg-white text-[#050505] hover:bg-white/90 text-sm font-medium rounded-lg px-4 disabled:opacity-50" data-testid="apply-coupon-btn">{applyingCoupon ? "…" : "Apply"}</button>
                      </div>
                    ) : (
                      <div className="bg-[#FF0066]/10 border border-[#FF0066]/30 rounded-lg px-3 py-2.5 flex items-center gap-2" data-testid="applied-coupon">
                        <Tag className="w-4 h-4 text-[#FF0066] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-mono font-semibold text-[#FF0066]">{appliedCoupon.code}</div>
                          <div className="text-[11px] text-[#FF0066]/80">Saved ₹{appliedCoupon.discount.toLocaleString("en-IN")}</div>
                        </div>
                        <button onClick={clearCoupon} className="p-1 rounded hover:bg-white/5 text-[#FF0066]" data-testid="remove-coupon"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                )}

                {enrolled ? (
                  <Link to="/dashboard/student" className="mt-5 w-full inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full px-4 py-3 transition">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Go to dashboard
                  </Link>
                ) : (user && (user.role === "teacher" || user.role === "admin")) ? (
                  <div className="mt-5 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-center" data-testid="staff-enrollment-blocked">
                    <ShieldCheck className="w-5 h-5 text-amber-300 mx-auto mb-1.5" />
                    <div className="text-sm font-semibold text-amber-200">Staff account detected</div>
                    <div className="text-xs text-amber-100/80 mt-1">
                      {user.role === "teacher" ? "Teachers" : "Admins"} ko enroll karne ki zaroorat nahi — aap{" "}
                      <Link to={`/dashboard/${user.role}`} className="underline hover:text-white">apne {user.role} dashboard</Link>{" "}
                      par sab kuch already access kar sakte ho.
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Payment plan selector — only shows plans admin enabled on this course */}
                    {!isFree && (() => {
                      const allowed = getAllowedPaymentOptions(course);
                      if (allowed.length <= 1) return null; // Only "full" → hide selector entirely
                      const finalAmt = appliedCoupon?.final ?? price;
                      const dueDays = Number(course?.balance_due_days) || 30;
                      const planConfig = {
                        full: { label: "Pay full now", sub: `${formatINR(finalAmt, { showFree: false })} · One-time`, badge: "Recommended" },
                        partial_50: { label: "Pay 50% now", sub: `${formatINR(Math.round(finalAmt * 0.5), { showFree: false })} now + ${formatINR(Math.round(finalAmt * 0.5), { showFree: false })} in ${dueDays} days`, badge: null },
                        installment_3: { label: "3 monthly EMIs", sub: `${formatINR(Math.round(finalAmt / 3), { showFree: false })} × 3 months`, badge: null },
                      };
                      return (
                        <div className="mt-5 space-y-1.5" data-testid="payment-plan-selector">
                          <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-1.5">Choose payment plan</div>
                          {allowed.map((key) => {
                            const p = planConfig[key];
                            if (!p) return null;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setPaymentPlan(key)}
                                className={`w-full text-left rounded-lg px-3 py-2.5 border transition ${paymentPlan === key ? "bg-[#FF0066]/15 border-[#FF0066]/60" : "bg-white/[0.02] border-white/10 hover:border-white/25"}`}
                                data-testid={`payment-plan-${key}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${paymentPlan === key ? "border-[#FF0066]" : "border-white/30"}`}>
                                      {paymentPlan === key && <div className="w-1.5 h-1.5 rounded-full bg-[#FF0066]" />}
                                    </div>
                                    <span className="text-xs font-semibold text-white">{p.label}</span>
                                  </div>
                                  {p.badge && <span className="text-[8px] uppercase tracking-wider font-bold text-amber-300 bg-amber-400/15 border border-amber-400/30 rounded px-1 py-0.5">{p.badge}</span>}
                                </div>
                                <div className="text-[11px] text-white/55 mt-0.5 ml-5">{p.sub}</div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <button onClick={handleEnroll} disabled={paying} className="mt-4 w-full inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-semibold rounded-full px-4 py-3.5 transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" data-testid="enroll-btn">
                      {paying ? "Processing…" : (paymentPlan === "full" ? "Enroll Now" : "Pay first installment")} {!paying && <ArrowRight className="w-4 h-4 ml-2" />}
                    </button>
                  </>
                )}
                <a href={`${WHATSAPP_BASE}${waText}`} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white font-medium rounded-full px-4 py-2.5 text-sm transition" data-testid="wa-btn">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> Ask on WhatsApp
                </a>
                <ShareButton id={id} type="course" title={course.title} className="mt-2" />

                <div className="mt-5 space-y-2 text-xs text-white/70">
                  {[
                    { icon: ShieldCheck, t: "7-day money-back guarantee" },
                    { icon: Award, t: "Government-recognised certificate" },
                    { icon: Headphones, t: "Lifetime doubt support" },
                    { icon: Briefcase, t: "Internship placement support" },
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-2"><b.icon className="w-3.5 h-3.5 text-[#FF0066] shrink-0" /> {b.t}</div>
                  ))}
                </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ===== What you'll learn ===== */}
      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">What you will learn</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white" style={displayStack}>Real skills, real projects, real impact</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {learnings.map((l, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#121212] border border-white/10 rounded-lg p-4">
                <CheckCircle2 className="w-5 h-5 text-[#FF0066] mt-0.5 shrink-0" />
                <div className="text-sm text-white/80">{typeof l === "string" ? l : (l.title || l.name)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Who should join ===== */}
      <section className="py-14 md:py-20 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">Who should join</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white" style={displayStack}>This course is built for you if you are…</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: GraduationCap, t: "Students" },
              { icon: Briefcase, t: "Business owners" },
              { icon: Users, t: "Freelancers" },
              { icon: FileText, t: "Job seekers" },
              { icon: Sparkles, t: "Creators" },
            ].map((p, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-5 text-center">
                <div className="w-11 h-11 mx-auto rounded-lg bg-[#FF0066]/10 flex items-center justify-center mb-3"><p.icon className="w-5 h-5 text-[#FF0066]" /></div>
                <div className="text-sm font-semibold text-white">{p.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Curriculum ===== */}
      {course.modules?.length > 0 && (
        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">Course curriculum</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={displayStack}>What's inside the course</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {course.modules.map((m, i) => (
                <AccordionItem key={i} value={`m-${i}`} className="bg-[#121212] border border-white/10 rounded-xl px-5">
                  <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-white hover:no-underline py-4" data-testid={`curriculum-${i}`}>
                    <span className="inline-flex items-center gap-3"><span className="w-7 h-7 rounded-md bg-[#FF0066]/10 text-[#FF0066] text-xs font-bold flex items-center justify-center shrink-0">{String(i + 1).padStart(2, "0")}</span>{typeof m === "string" ? m : (m.title || m.name || `Module ${i + 1}`)}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-white/70 leading-relaxed pb-4 pl-10">
                    {typeof m === "string" ? "Hands-on practical training with real campaigns and projects." : (m.description || m.summary || "Hands-on practical training with real campaigns and projects.")}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* ===== Bonuses ===== */}
      <section className="py-14 md:py-20 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">Bonus inclusions</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={displayStack}>Worth ₹25,000+ — included free with your enrollment</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "Premium templates", d: "Landing pages, email sequences, ad creatives — ready to use." },
              { t: "Marketing tools access", d: "Free access to Canva Pro, SEO tools and analytics during course." },
              { t: "Class recordings", d: "Lifetime access to all live class recordings via student dashboard." },
              { t: "Private community", d: "Lifetime WhatsApp + Telegram group with mentor and peers." },
              { t: "Internship support", d: "First freelancing project written guarantee + agency referrals." },
              { t: "1-on-1 mentorship", d: "Personal review calls for resume, portfolio and career path." },
            ].map((b, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <div className="w-9 h-9 rounded-lg bg-[#FF0066] text-white flex items-center justify-center mb-3"><Sparkles className="w-4 h-4" /></div>
                <div className="text-sm font-semibold text-white mb-1">{b.t}</div>
                <div className="text-xs text-white/60 leading-relaxed">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Custom Mentor Cards (admin) ===== */}
      {pageCfg.mentor_blocks?.length > 0 && (
        <section className="py-14 md:py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">Meet your mentors</div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={displayStack}>Industry expert mentors</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pageCfg.mentor_blocks.map((m, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-5 flex flex-col items-center text-center" data-testid={`mentor-card-${i}`}>
                  {m.photo && <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-[#FF0066]/30 mb-3" />}
                  <div className="font-bold text-white">{m.name}</div>
                  <div className="text-xs text-[#FF0066] uppercase tracking-wider mt-0.5">{m.role}</div>
                  {m.bio && <p className="text-sm text-white/70 mt-3">{m.bio}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Custom Sections (admin) ===== */}
      {pageCfg.custom_sections?.length > 0 && pageCfg.custom_sections.map((s, i) => (
        s.title || s.body ? (
          <section key={i} className="py-12 md:py-16 border-t border-white/5" data-testid={`custom-section-${i}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              {s.title && <h2 className="text-2xl md:text-3xl font-bold text-white mb-5" style={displayStack}>{s.title}</h2>}
              {s.body && <div className="text-base text-white/75 leading-relaxed whitespace-pre-wrap">{s.body}</div>}
            </div>
          </section>
        ) : null
      ))}

      {/* ===== Testimonials ===== */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2">Student stories</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={displayStack}>What our students say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {(pageCfg.testimonials?.length > 0 ? pageCfg.testimonials : [
              { name: "Priya Sharma", role: "SEO Freelancer", text: "Within 2 months I landed my first client at ₹15,000/month. The live practical training made all the difference." },
              { name: "Rahul Verma", role: "Marketing Executive", text: "Got placed at a digital agency with 60% salary jump. The resume review and interview prep sessions were game-changers." },
              { name: "Aakash Singh", role: "Business Owner", text: "Sir taught me how to run my own Meta Ads. Now I generate 30+ leads daily and stopped depending on agencies." },
            ]).map((t, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-1 text-amber-400 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" />)}</div>
                <p className="text-sm text-white/80 leading-relaxed mb-4">"{t.text}"</p>
                <div className="text-sm font-semibold text-white">{t.name}</div>
                <div className="text-xs text-white/50">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-14 md:py-20 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-2 text-center">FAQ</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8" style={displayStack}>Quick answers</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {(pageCfg.faqs?.length > 0 ? pageCfg.faqs : [
              { q: "How are the classes conducted?", a: "Classes are live online via Zoom (you can also join from our Indore centre). Each class has a recording, notes and an assignment available in your student dashboard." },
              { q: "Do I need any prior experience?", a: "No. The course starts from absolute basics. We cover everything — from creating your first website to running advanced ad campaigns." },
              { q: "Can I get my money back?", a: "Yes. We offer a 7-day no-questions-asked refund policy. If you're not happy within the first week, we refund 100%." },
              { q: "Will I get a certificate?", a: "Yes — a government-recognised completion certificate after you finish all assignments and the final project." },
              { q: "How does payment work?", a: "Secure online payment via Razorpay — UPI, debit/credit cards, net banking and wallets are all supported. EMI options available for select courses." },
            ]).map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="bg-[#121212] border border-white/10 rounded-xl px-5">
                <AccordionTrigger className="text-left text-sm md:text-base font-medium text-white hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-white/70 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== Sticky mobile bottom CTA ===== */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#050505] border-t border-white/10 px-4 py-3 flex items-center gap-3 shadow-2xl">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-white/50">{appliedCoupon ? "After coupon" : hasDiscount ? "Limited offer" : "Course fee"}</div>
          <div className="text-lg font-extrabold text-white" style={displayStack} data-testid="mobile-sticky-price">{isFree ? "Free" : formatINR(appliedCoupon?.final ?? price, { showFree: false })}</div>
        </div>
        {enrolled ? (
          <Link to="/dashboard/student" className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-5 py-3 text-sm inline-flex items-center gap-1" data-testid="mobile-enrolled-cta">Open <ArrowRight className="w-4 h-4" /></Link>
        ) : (user && (user.role === "teacher" || user.role === "admin")) ? (
          <Link to={`/dashboard/${user.role}`} className="bg-white/10 border border-white/15 text-white font-semibold rounded-full px-5 py-3 text-sm inline-flex items-center gap-1" data-testid="mobile-staff-cta">
            Go to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button onClick={handleEnroll} disabled={paying} className="bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-semibold rounded-full px-5 py-3 text-sm inline-flex items-center gap-1" data-testid="mobile-enroll-cta">
            {paying ? "…" : "Enroll Now"} {!paying && <ArrowRight className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
